import { Streamdown } from 'streamdown';
import { cn } from '@/lib/utils';

interface MarkdownMessageProps {
  content: string;
  className?: string;
}

/**
 * Markdown d'un missatge de xat.
 *
 * Per què existeix: el xat pintava `<ReactMarkdown>` pelat dins d'un `div` amb
 * `prose prose-sm dark:prose-invert`. Cap de les dues coses feia res:
 *  - sense `remark-gfm`, `react-markdown` no parseja taules (sortien com a text amb barres),
 *  - les classes `prose` són de `@tailwindcss/typography`, que aquest projecte no té
 *    instal·lat ni registrat a `src/index.css` (Tailwind v4, només `tailwindcss-animate`).
 *
 * Per què `streamdown` i no `react-markdown`: el xat de Miranda pinta el Markdown mentre
 * arriba per streaming, i `react-markdown` renderitza el Markdown incomplet tal com el rep
 * (una taula a mig arribar es veu trencada i «salta» quan es tanca). `streamdown` és el
 * mateix renderitzador amb GFM ja integrat i reparació del Markdown no terminat. El manual
 * (`components/docs/MarkdownViewer.tsx`) segueix amb `react-markdown` + `remark-gfm`: allà
 * el contingut és estàtic i no cal res d'això.
 *
 * El mapa de `components` és una adaptació del de `components/docs/MarkdownViewer.tsx`
 * (mateixa estructura h1…td). No es comparteix perquè allà són mides de manual amb
 * colors `gray-*` fixos i enllaços que naveguen pel manual; aquí calen mides de xat i
 * els tokens de tema (`muted`, `border`, `foreground`). Si canvies un dels dos, mira l'altre.
 *
 * `controls={false}` desactiva els botons de copiar/descarregar que Streamdown afegeix per
 * defecte a taules, codi i imatges: aquí el mapa de `components` ja pinta aquests elements i
 * els botons no formen part del disseny del xat. `space-y-0` anul·la el `space-y-4` que
 * Streamdown posa al seu contenidor (tailwind-merge el resol): l'espaiat entre blocs del xat
 * el donen els `mb-2` del mapa de `components`, com abans. El `[&>*:last-child]:mb-0` que hi
 * havia aquí ja el posa Streamdown al mateix contenidor.
 */
export function MarkdownMessage({ content, className }: MarkdownMessageProps) {
  return (
    <div className={cn('text-sm leading-relaxed', className)}>
      <Streamdown
        className="space-y-0"
        controls={false}
        components={{
          h1: ({ children }) => (
            <h1 className="text-base font-bold mt-3 mb-2 first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-xs font-semibold uppercase tracking-wide mt-2 mb-1 first:mt-0">
              {children}
            </h4>
          ),
          p: ({ children }) => <p className="mb-2 whitespace-pre-wrap">{children}</p>,
          ul: ({ children }) => (
            <ul className="list-disc pl-5 space-y-0.5 mb-2">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 space-y-0.5 mb-2">{children}</ol>
          ),
          li: ({ children }) => <li className="marker:text-muted-foreground">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children }) => (
            <a
              href={href}
              className="underline underline-offset-2 hover:opacity-80"
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          ),
          code: ({ className: codeClassName, children, node: _node, ...props }) => {
            const isInline = !codeClassName;
            if (isInline) {
              return (
                <code className="bg-background/60 px-1 py-0.5 rounded text-[0.8em] font-mono">
                  {children}
                </code>
              );
            }
            return (
              <code className={cn('block font-mono', codeClassName)} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-background/60 border rounded-md p-2 mb-2 overflow-x-auto text-xs">
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 pl-3 my-2 text-muted-foreground italic">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full border rounded-md text-xs border-collapse">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-background/60">{children}</thead>,
          th: ({ children }) => (
            <th className="px-2 py-1 text-left font-semibold border whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => <td className="px-2 py-1 align-top border">{children}</td>,
          hr: () => <hr className="my-3" />,
        }}
      >
        {content}
      </Streamdown>
    </div>
  );
}

export default MarkdownMessage;
